import type { NpcDifficulty, RaceEventMode, RacerSnapshot, TimeOfDay, Weather } from '../shared/types.js';
import {
  NIGHT_TOUR_ID,
  NIGHT_TOUR_RIVALS,
  isStreetTourId,
  type StreetTourId,
} from '../shared/night-tour-rivals.js';
import {
  readLocalCareerSection,
  updateLocalCareerSection,
} from './local-career-storage.js';

export interface NightTourEvent {
  id: string;
  title: string;
  subtitle: string;
  mapId: string;
  eventMode: RaceEventMode;
  timeOfDay: TimeOfDay;
  weather: Weather;
  npcDifficulty: NpcDifficulty;
  traffic: boolean;
  featuredRival?: string;
}

export interface NightTourResult {
  stage: number;
  raceId: string;
  place: number;
  points: number;
  rivalPlace: number;
  rivalPoints: number;
}

export interface NightTourDefinition {
  id: StreetTourId;
  name: string;
  kicker: string;
  reward: string;
  accent: string;
  events: readonly NightTourEvent[];
}

interface NightTourArchive {
  eventIndex: number;
  playerPoints: number;
  rivalPoints: number;
  results: NightTourResult[];
  completed: boolean;
  completions: number;
  bestPoints: number;
  updatedAt: number;
}

export interface NightTourState {
  version: 1;
  tourId: StreetTourId;
  eventIndex: number;
  playerPoints: number;
  rivalPoints: number;
  results: NightTourResult[];
  completed: boolean;
  completions: number;
  bestPoints: number;
  updatedAt: number;
  archives: Partial<Record<StreetTourId, NightTourArchive>>;
}

export const NIGHT_TOUR_EVENTS: readonly NightTourEvent[] = [
  {
    id: 'bern-opening-shot',
    title: 'OLD TOWN OPENING',
    subtitle: 'A one-lap dawn sprint through Bern.',
    mapId: 'bern-altstadt',
    eventMode: 'sprint',
    timeOfDay: 'dawn',
    weather: 'haze',
    npcDifficulty: 'rookie',
    traffic: false,
    featuredRival: NIGHT_TOUR_RIVALS[0].name,
  },
  {
    id: 'luzern-lake-pressure',
    title: 'LAKE PRESSURE',
    subtitle: 'Three laps as the sun drops over Luzern.',
    mapId: 'luzern-lakeside',
    eventMode: 'circuit',
    timeOfDay: 'sunset',
    weather: 'clear',
    npcDifficulty: 'sport',
    traffic: true,
    featuredRival: NIGHT_TOUR_RIVALS[1].name,
  },
  {
    id: 'hannover-style-line',
    title: 'CITY STYLE LINE',
    subtitle: 'Build the biggest chain around the Maschsee.',
    mapId: 'hannover-maschsee',
    eventMode: 'style-rush',
    timeOfDay: 'night',
    weather: 'clear',
    npcDifficulty: 'sport',
    traffic: false,
    featuredRival: NIGHT_TOUR_RIVALS[2].name,
  },
  {
    id: 'berlin-final-heat',
    title: 'CAPITAL HEAT',
    subtitle: 'Bank your score before Mika takes Berlin.',
    mapId: 'berlin-capital',
    eventMode: 'heat-run',
    timeOfDay: 'night',
    weather: 'rain',
    npcDifficulty: 'pro',
    traffic: true,
    featuredRival: NIGHT_TOUR_RIVALS[3].name,
  },
] as const;

export const MOUNTAIN_PULSE_EVENTS: readonly NightTourEvent[] = [
  {
    id: 'alpine-first-ascent',
    title: 'FIRST ASCENT',
    subtitle: 'A cold dawn sprint through the alpine switchbacks.',
    mapId: 'alpine-midnight',
    eventMode: 'sprint',
    timeOfDay: 'dawn',
    weather: 'haze',
    npcDifficulty: 'sport',
    traffic: false,
    featuredRival: NIGHT_TOUR_RIVALS[4].name,
  },
  {
    id: 'sunspire-vertical-line',
    title: 'VERTICAL LINE',
    subtitle: 'Own the sunset climb and commit to the summit route.',
    mapId: 'sunspire-pass',
    eventMode: 'circuit',
    timeOfDay: 'sunset',
    weather: 'clear',
    npcDifficulty: 'sport',
    traffic: false,
    featuredRival: NIGHT_TOUR_RIVALS[5].name,
  },
  {
    id: 'mirror-last-one-up',
    title: 'LAST ONE UP',
    subtitle: 'Elimination pressure across the raised lake circuit.',
    mapId: 'mirror-lake-circuit',
    eventMode: 'elimination',
    timeOfDay: 'night',
    weather: 'rain',
    npcDifficulty: 'pro',
    traffic: false,
    featuredRival: NIGHT_TOUR_RIVALS[6].name,
  },
  {
    id: 'luzern-summit-finale',
    title: 'ALPINE AFTERGLOW',
    subtitle: 'A clean lakeside finale with Lux waiting up front.',
    mapId: 'luzern-lakeside',
    eventMode: 'circuit',
    timeOfDay: 'sunset',
    weather: 'clear',
    npcDifficulty: 'pro',
    traffic: true,
    featuredRival: NIGHT_TOUR_RIVALS[7].name,
  },
] as const;

export const OUTLAW_FREQUENCY_EVENTS: readonly NightTourEvent[] = [
  {
    id: 'neon-signal',
    title: 'PIRATE SIGNAL',
    subtitle: 'Build the loudest style chain under the neon towers.',
    mapId: 'neon-district',
    eventMode: 'style-rush',
    timeOfDay: 'night',
    weather: 'rain',
    npcDifficulty: 'sport',
    traffic: false,
    featuredRival: NIGHT_TOUR_RIVALS[8].name,
  },
  {
    id: 'redline-dust-run',
    title: 'DUST RUN',
    subtitle: 'One lap, no excuses, through a live canyon storm.',
    mapId: 'redline-canyon',
    eventMode: 'sprint',
    timeOfDay: 'sunset',
    weather: 'sand',
    npcDifficulty: 'sport',
    traffic: false,
    featuredRival: NIGHT_TOUR_RIVALS[9].name,
  },
  {
    id: 'azure-last-signal',
    title: 'COASTAL BLACKOUT',
    subtitle: 'Survive elimination traffic along the midnight coast.',
    mapId: 'azure-coast',
    eventMode: 'elimination',
    timeOfDay: 'night',
    weather: 'clear',
    npcDifficulty: 'pro',
    traffic: true,
    featuredRival: NIGHT_TOUR_RIVALS[10].name,
  },
  {
    id: 'berlin-outlaw-final',
    title: 'NO LAST CALL',
    subtitle: 'Bank the final Berlin heat before Nyx takes everything.',
    mapId: 'berlin-capital',
    eventMode: 'heat-run',
    timeOfDay: 'night',
    weather: 'rain',
    npcDifficulty: 'pro',
    traffic: true,
    featuredRival: NIGHT_TOUR_RIVALS[11].name,
  },
] as const;

export const STREET_TOURS: readonly NightTourDefinition[] = [
  {
    id: 'city-lights',
    name: 'CITY LIGHTS',
    kicker: 'EUROPEAN NIGHT RUN',
    reward: 'CITY LIGHTS PAINT',
    accent: '#ff4fa3',
    events: NIGHT_TOUR_EVENTS,
  },
  {
    id: 'mountain-pulse',
    name: 'MOUNTAIN PULSE',
    kicker: 'VERTICAL RIVAL SERIES',
    reward: 'SUMMIT ICE PAINT',
    accent: '#57e9ff',
    events: MOUNTAIN_PULSE_EVENTS,
  },
  {
    id: 'outlaw-frequency',
    name: 'OUTLAW FREQUENCY',
    kicker: 'NO-RULES RADIO TOUR',
    reward: 'OUTLAW VIOLET PAINT',
    accent: '#d946ef',
    events: OUTLAW_FREQUENCY_EVENTS,
  },
] as const;

const STORAGE_KEY = 'nightshift:night-tour:v1';
const POINTS = [10, 7, 5, 3, 2, 1] as const;

export function createNightTourState(
  previous?: Pick<NightTourState, 'completions' | 'bestPoints' | 'archives'>,
  tourId: StreetTourId = NIGHT_TOUR_ID,
): NightTourState {
  return {
    version: 1,
    tourId,
    eventIndex: 0,
    playerPoints: 0,
    rivalPoints: 0,
    results: [],
    completed: false,
    completions: previous?.completions ?? 0,
    bestPoints: previous?.bestPoints ?? 0,
    updatedAt: Date.now(),
    archives: previous?.archives ?? {},
  };
}

export function currentNightTourEvent(state: NightTourState): NightTourEvent | null {
  return state.completed ? null : nightTourEvents(state)[state.eventIndex] ?? null;
}

export function nightTourDefinition(tourId: StreetTourId): NightTourDefinition {
  return STREET_TOURS.find((tour) => tour.id === tourId) ?? STREET_TOURS[0]!;
}

export function nightTourEvents(state: Pick<NightTourState, 'tourId'>): readonly NightTourEvent[] {
  return nightTourDefinition(state.tourId).events;
}

export function selectNightTour(state: NightTourState, tourId: StreetTourId): NightTourState {
  if (state.tourId === tourId) return state;
  const archives = {
    ...state.archives,
    [state.tourId]: archiveFromState(state),
  };
  const target = archives[tourId];
  if (!target) return createNightTourState({ completions: 0, bestPoints: 0, archives }, tourId);
  return {
    version: 1,
    tourId,
    ...target,
    archives,
  };
}

export function nightTourCompletions(state: NightTourState, tourId: StreetTourId): number {
  return tourId === state.tourId
    ? state.completions
    : state.archives[tourId]?.completions ?? 0;
}

/** Prepares a repeat championship without mutating or persisting the completed
 * result. The caller commits this candidate only after the new race exists. */
export function nightTourStateForStart(state: NightTourState): NightTourState {
  return state.completed ? createNightTourState(state, state.tourId) : state;
}

export function recordNightTourResult(
  state: NightTourState,
  raceId: string,
  stage: number,
  local: RacerSnapshot,
  racers: readonly RacerSnapshot[],
  persist = true,
): NightTourState {
  if (
    state.completed
    || local.finishedAt === null
    || stage !== state.eventIndex
    || state.results.some((result) => result.raceId === raceId)
  ) return state;
  const events = nightTourEvents(state);
  const featured = events[stage]?.featuredRival ?? 'Mika';
  const rival = racers.find((racer) => racer.npc && racer.name.toLowerCase() === featured.toLowerCase())
    ?? racers.find((racer) => racer.npc);
  const result: NightTourResult = {
    stage,
    raceId: safeRaceId(raceId),
    place: safePlace(local.place),
    points: pointsForPlace(local.place),
    rivalPlace: safePlace(rival?.place ?? racers.length),
    rivalPoints: pointsForPlace(rival?.place ?? racers.length),
  };
  const eventIndex = Math.min(events.length, state.eventIndex + 1);
  const playerPoints = state.playerPoints + result.points;
  const rivalPoints = state.rivalPoints + result.rivalPoints;
  const completed = eventIndex >= events.length;
  const next: NightTourState = {
    ...state,
    eventIndex,
    playerPoints,
    rivalPoints,
    results: [...state.results, result],
    completed,
    completions: state.completions + Number(completed),
    bestPoints: completed ? Math.max(state.bestPoints, playerPoints) : state.bestPoints,
    updatedAt: Date.now(),
  };
  if (persist) saveNightTour(next);
  return next;
}

export function pointsForPlace(placeValue: number): number {
  return POINTS[Math.max(0, Math.min(POINTS.length - 1, safePlace(placeValue) - 1))] ?? 1;
}

export function parseNightTour(raw: string | null): NightTourState {
  const defaultFallback = createNightTourState();
  if (!raw) return defaultFallback;
  try {
    const value = JSON.parse(raw) as Partial<NightTourState>;
    if (value.version !== 1) return defaultFallback;
    const tourId = isStreetTourId(value.tourId) ? value.tourId : NIGHT_TOUR_ID;
    const fallback = createNightTourState(undefined, tourId);
    const progress = parseTourProgress(value, nightTourDefinition(tourId).events.length, fallback.updatedAt);
    const archives: Partial<Record<StreetTourId, NightTourArchive>> = {};
    if (isRecord(value.archives)) {
      for (const definition of STREET_TOURS) {
        const archived = value.archives[definition.id];
        if (!isRecord(archived)) continue;
        archives[definition.id] = parseTourProgress(archived, definition.events.length, fallback.updatedAt);
      }
    }
    return {
      version: 1,
      tourId,
      ...progress,
      archives,
    };
  } catch {
    return defaultFallback;
  }
}

export function loadNightTour(): NightTourState {
  try {
    const career = readLocalCareerSection('nightTour');
    if (career !== undefined) return parseNightTour(JSON.stringify(career));
    return parseNightTour(localStorage.getItem(STORAGE_KEY));
  } catch {
    return createNightTourState();
  }
}

export function saveNightTour(state: NightTourState): void {
  const value = { ...state, version: 1 as const, updatedAt: Date.now() };
  const envelopeUpdated = updateLocalCareerSection('nightTour', value);
  if (envelopeUpdated !== null) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // The tour remains playable in memory when storage is unavailable.
  }
}

function isTourResult(value: unknown): value is NightTourResult {
  if (!value || typeof value !== 'object') return false;
  const result = value as Partial<NightTourResult>;
  return Number.isInteger(result.stage)
    && Number(result.stage) >= 0
    && Number(result.stage) < 32
    && typeof result.raceId === 'string'
    && Number.isFinite(result.place)
    && Number.isFinite(result.points)
    && Number.isFinite(result.rivalPlace)
    && Number.isFinite(result.rivalPoints);
}

function archiveFromState(state: NightTourState): NightTourArchive {
  return {
    eventIndex: state.eventIndex,
    playerPoints: state.playerPoints,
    rivalPoints: state.rivalPoints,
    results: state.results,
    completed: state.completed,
    completions: state.completions,
    bestPoints: state.bestPoints,
    updatedAt: state.updatedAt,
  };
}

function parseTourProgress(
  source: Partial<NightTourArchive>,
  eventCount: number,
  fallbackUpdatedAt: number,
): NightTourArchive {
  const candidates = Array.isArray(source.results) ? source.results.filter(isTourResult) : [];
  const results: NightTourResult[] = [];
  const raceIds = new Set<string>();
  for (const result of candidates) {
    if (result.stage !== results.length || raceIds.has(result.raceId)) continue;
    raceIds.add(result.raceId);
    results.push({
      stage: result.stage,
      raceId: safeRaceId(result.raceId),
      place: safePlace(result.place),
      points: pointsForPlace(result.place),
      rivalPlace: safePlace(result.rivalPlace),
      rivalPoints: pointsForPlace(result.rivalPlace),
    });
    if (results.length >= eventCount) break;
  }
  const eventIndex = results.length;
  const playerPoints = results.reduce((sum, result) => sum + result.points, 0);
  const rivalPoints = results.reduce((sum, result) => sum + result.rivalPoints, 0);
  const completed = eventIndex >= eventCount;
  return {
    eventIndex,
    playerPoints,
    rivalPoints,
    results,
    completed,
    completions: Math.max(Number(completed), integer(source.completions, 0, 10_000)),
    bestPoints: Math.max(completed ? playerPoints : 0, integer(source.bestPoints, 0, 10_000)),
    updatedAt: finite(source.updatedAt, fallbackUpdatedAt),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function safeRaceId(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 96) || `tour-${Date.now()}`;
}

function safePlace(value: number): number {
  return integer(value, 1, 99);
}

function integer(value: unknown, minimum: number, maximum: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(minimum, Math.min(maximum, Math.round(parsed))) : minimum;
}

function finite(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}
